// src/FreelancerPublicView.js
// Vista profilo freelance vista da altri freelance — niente team suggerito, preferiti, alert
import { fetchJson, authHeaders, fileUrl } from './api';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ChatIcon from '@mui/icons-material/Chat';
import UserProfilePanel from './UserProfilePanel';
import AvailabilityCalendar from './AvailabilityCalendar';

// Helpers timezone-safe
function buildBusySetFromProduzioni(list = []) {
  const set = new Set();
  if (!Array.isArray(list)) return set;

  const parse = (s) => {
    if (!s) return null;
    const only = String(s).split('T')[0];
    const [y, m, d] = only.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const ymd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  for (const p of list) {
    const s = parse(p?.data_inizio_contratto) || parse(p?.data_inizio_prevista) || parse(p?.data_inizio) || parse(p?.startDate);
    const e = parse(p?.data_fine_contratto) || parse(p?.data_fine_prevista) || parse(p?.data_fine) || parse(p?.endDate) || s;
    if (!s) continue;

    let d = new Date(s.getFullYear(), s.getMonth(), s.getDate());
    const end = e && e >= s ? new Date(e.getFullYear(), e.getMonth(), e.getDate()) : d;
    while (d.getTime() <= end.getTime()) {
      set.add(ymd(d));
      d.setDate(d.getDate() + 1);
    }
  }
  return set;
}

export default function FreelancerPublicView({ freelancerId, token, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  const effectiveToken =
    token ||
    (typeof localStorage !== 'undefined' && (localStorage.getItem('token') || localStorage.getItem('authToken'))) ||
    '';

  // Avvia conversazione e redirect a /messages
  const onMessage = async () => {
    try {
      const otherUserId = Number(profile?.user_id);
      if (!otherUserId) {
        alert('Impossibile avviare chat: user_id mancante.');
        return;
      }
      const res = await fetchJson('/messages/conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${effectiveToken}`,
        },
        body: JSON.stringify({ otherUserId }),
      });
      const cid = res?.id;
      if (!cid) { alert('Errore: conversazione non creata.'); return; }
      navigate(`/messages?cid=${cid}`);
    } catch (e) {
      alert(e?.message || 'Errore avvio conversazione');
    }
  };

  const busySet = React.useMemo(() => {
    const raw = profile?.maestranze ?? profile?.produzioni ?? profile?.productions ?? [];
    return buildBusySetFromProduzioni(Array.isArray(raw) ? raw : []);
  }, [profile]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchJson(`/freelancers/${freelancerId}`, {
          headers: authHeaders(effectiveToken),
        });
        if (!alive) return;
        setProfile(data || null);
      } catch (e) {
        if (!alive) return;
        setError('Errore di rete');
        setProfile(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [freelancerId, token]);

  if (loading) return <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>;
  if (error || !profile) return <Box sx={{ p: 2 }}><Typography color="error">{error || 'Profilo non disponibile'}</Typography></Box>;

  const Header = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2 }}>
      {onBack && (
        <IconButton onClick={onBack} size="small" sx={{ mr: 0.5 }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {`${profile?.nome || ''} ${profile?.cognome || ''}`.trim() || '—'}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
          {(Array.isArray(profile?.ruoli) && profile.ruoli.length
            ? profile.ruoli
            : profile?.ruolo ? [profile.ruolo] : []
          ).slice(0, 3).map((r) => (
            <Chip
              key={r}
              size="small"
              icon={<WorkOutlineIcon sx={{ fontSize: 16 }} />}
              label={r}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Stack>
      </Box>

      {/* Invia messaggio — visibile solo in questa vista pubblica */}
      <Button
        variant="outlined"
        startIcon={<ChatIcon />}
        onClick={onMessage}
        sx={{ borderRadius: 999, whiteSpace: 'nowrap' }}
      >
        Invia messaggio
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {Header}
        <Divider />
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <UserProfilePanel
            initialProfile={profile}
            token={effectiveToken}
            endpointProfile={`/freelancers/${freelancerId}`}
            endpointAvailability={`/freelancers/${freelancerId}/availability`}
            busyDays={busySet}
            readOnly
            viewerType="freelance"
            onMessage={onMessage}
          />
        </Box>
      </Paper>
    </Box>
  );
}
